import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationService } from '@onlive.site/front-core';
import { useSettingValue } from 'common/hooks/useSettingValue';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import { startTitleBlink, stopTitleBlink } from './titleBlink';

// Falls back to a generic bell for any request type that's neither a call
// nor a chat message — future request kinds get a sensible icon for free
// instead of silently reading as a chat message.
const REQUEST_TYPE_EMOJI = { video: '📞', chat: '💬', generic: '🔔' };
const emojiFor = (type) => REQUEST_TYPE_EMOJI[type] ?? REQUEST_TYPE_EMOJI.generic;

/**
 * Drives the blinking tab title and native push notification for pending
 * mf-ichat requests, independently of whether the floating alert card itself
 * is on screen. Reads `alert.data.tabAlert` off the same `alerts` list
 * VmsAlerts renders — that field isn't part of the VmsAlert contract from
 * @onlive.site/mf, it just rides inside the opaque `data` payload the ichat
 * micro-frontend already sends with OPEN_ALERT.
 *
 * The two strategies are independently on/off per alert (mf-ichat decides
 * which apply — a call gets both, a chat message only the title blink), and
 * each is active for as long as ANY pending alert requests it: no counter,
 * matching the floating alert's own "gone once resolved" lifecycle. No
 * favicon badge: the blinking title icon is signal enough on its own.
 */
export function useTabAlerts(alerts) {
    const { t } = useTranslation();
    const ichatNotifications = useSettingValue(SettingsKey.ICHAT_NOTIFICATIONS);
    const pushEnabled =
        ichatNotifications?.[SettingsKey.INCOMING_CHAT_REQUEST]?.[SettingsKey.ENABLE_BROWSER_PUSH_NOTIFICATION];
    // Ids already pushed as a native notification, so a re-render (or the
    // alert simply staying pending) never re-notifies for the same request.
    const notifiedIdsRef = useRef(new Set());

    useEffect(() => {
        const tabAlerts = alerts.filter((alert) => alert.data?.tabAlert);

        const titleAlert = tabAlerts.find((alert) => alert.data.tabAlert.title);
        if (titleAlert) {
            startTitleBlink(emojiFor(titleAlert.data.type));
        } else {
            stopTitleBlink();
        }

        const activeIds = new Set(tabAlerts.map((alert) => alert.id));
        notifiedIdsRef.current.forEach((id) => {
            if (!activeIds.has(id)) notifiedIdsRef.current.delete(id);
        });

        if (pushEnabled && 'Notification' in window && Notification.permission === 'granted') {
            tabAlerts
                .filter((alert) => alert.data.tabAlert.nativeNotification && !notifiedIdsRef.current.has(alert.id))
                .forEach((alert) => {
                    notifiedIdsRef.current.add(alert.id);
                    const notification = NotificationService.notify('push_notification', {
                        title: alert.data.name || t('ichat.notifications.tabAlert.fallbackTitle'),
                        body: t(
                            alert.data.type === 'video'
                                ? 'ichat.notifications.tabAlert.call.body'
                                : 'ichat.notifications.tabAlert.message.body',
                        ),
                    });
                    // Bringing the tab forward is enough: the floating alert card
                    // already lives there, waiting for Accept/Dismiss.
                    notification.onclick = () => window.focus();
                });
        }
    }, [alerts, pushEnabled, t]);

    // Belt-and-suspenders: leave no stuck blink if this component ever unmounts.
    useEffect(() => stopTitleBlink, []);
}

export default useTabAlerts;
